/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-10-10 00:00:00
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import queryString from 'query-string';
import { Modal } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { routes } from 'src/routes';
import history from 'src/routes/history';

import { nowTimestamp, getShop } from 'src/helpers';
import { allowToSubscribeBFCMCode } from 'src/helpers/subscription-helper';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';

import BFCM2021OfferImage from 'src/assets/images/common/BFCM-2021-offer.png';
import BFCM2021OfferDeImage from 'src/assets/images/common/BFCM-2021-offer-de.png';

type Props = {
  onDismiss: Function
};

type ComposedProps = Props & WithI18nProps;
type State = {
  dismissedBanner: boolean
};

class BFCM2021OfferModal extends Component<ComposedProps, State> {
  state = {
    dismissedBanner: true
  };

  componentDidMount() {
    // Show banner
    this.checkDismissedBanner();
  }

  render() {
    const { i18n, tenantApp } = this.props;
    const { dismissedBanner } = this.state;
    const appLanguage = _.get(tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE);
    const image = appLanguage === 'de' ? BFCM2021OfferDeImage : BFCM2021OfferImage;

    return (
      <Modal
        open={!dismissedBanner}
        onClose={this.onDismiss}
        title={i18n.translate('AdminHTML.Common.BFCM2021OfferModal.title')}
        primaryAction={{
          content: i18n.translate('AdminHTML.Common.BFCM2021OfferModal.action'),
          onAction: () => {
            this.goToSubscriptionPage();
          }
        }}>
        <Modal.Section>
          <img
            className="BC-Illustration-Image__Notification"
            src={image}
            alt="Affiliate program present"
          />
        </Modal.Section>
      </Modal>
    );
  }

  // Get dismissed banner status
  // Condition:
  // - Show popup after customers are in our app 20s
  // - Show once per day for each logged-in user of a tenantID until users turn to paid customers.
  // - Only shown for trial users (current, new, expired)
  checkDismissedBanner = () => {
    const { tenantApp = {}, infoRequest = {} } = this.props;

    // Show offer modal
    const pathName = infoRequest.pathname;
    // Get charge_id from query params
    // Skip redirect to Plan page if status is expired or exceeded
    const queries = queryString.parse(infoRequest.search);
    const { charge_id } = queries;

    // Check allow using discount code
    const allowedBFCM2021 = allowToSubscribeBFCMCode(tenantApp);

    // Check display time
    const allowedDisplay = this.allowDisplay();

    if (
      ![
        '/plan-discount',
        '/plan-migration',
        '/oauth',
        '/oauth/finalize',
        '/choose-layout',
        '/initializing',
        '/setup-complete',
        '/verify-setup'
      ].includes(pathName) &&
      !pathName.includes('/plan-discount-large/') &&
      !charge_id &&
      allowedBFCM2021 &&
      allowedDisplay
    ) {
      // Show banner after 20s logged-in
      setTimeout(() => {
        this.setState({ dismissedBanner: false });

        // Track display time
        try {
          localStorage.setItem(`${getShop()}-BFCM-display-banner-time`, nowTimestamp());
        } catch (error) {}
      }, 20000);
    }
  };

  // Show once per day for each logged-in user of a tenantID until users turn to paid customers.
  allowDisplay = () => {
    let allowedDisplay = false;
    let lastShowTime = 0;
    const now = nowTimestamp();
    const startDate = 1634281200; // 02:00 PM, Oct 15, 2021
    const endDate = 1635699600; // Nov 01, 2021

    // Get last show time
    try {
      lastShowTime = localStorage.getItem(`${getShop()}-BFCM-display-banner-time`);
      lastShowTime = lastShowTime ? _.toNumber(lastShowTime) * 1000 : 0;
    } catch (error) {}

    const lastShowDate = new Date(lastShowTime);
    const currentDate = new Date();

    if (
      now >= startDate &&
      now <= endDate &&
      (!lastShowTime ||
        (lastShowDate.getMonth() === currentDate.getMonth() &&
          lastShowDate.getDate() < currentDate.getDate()) ||
        lastShowDate.getMonth() < currentDate.getMonth())
    ) {
      allowedDisplay = true;
    }

    return allowedDisplay;
  };

  // Handle hide banner
  onDismiss = () => {
    // Hide banner
    this.setState({ dismissedBanner: true });
  };

  // Handle go to Subscription page
  goToSubscriptionPage = () => {
    // Hide modal
    this.setState({ dismissedBanner: true });

    // Go to Subscription page
    history.push(
      `${routes.subscription.discount()}?itm_source=PFS+app&itm_medium=Popup&itm_campaign=BFCM2021`
    );
  };
}

export default withI18n()(BFCM2021OfferModal);
